"use client";

import { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { HeartIcon, ToteIcon } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

interface ProductActionButtonsProps {
  cartItemCount?: number;
  isChatLoading?: boolean;
  onChatClick?: () => void;
  isWishlisted?: boolean;
  isWishlistLoading?: boolean;
  onWishlistClick?: () => void;
  /** 장바구니·구매하기 버튼을 감싼다 — 상세 하단 바는 DrawerTrigger 로 감싸
   *  드로어를 연다. 없으면 버튼 그대로. */
  wrapDrawerTrigger?: (button: ReactNode) => ReactNode;
  /** 안내용 그림 — 동작 없이 보여만 주고(inert), 문의하기만 강조한다.
   *  플랫폼 문의 시트가 "상품 문의는 여기서" 를 보여줄 때 쓴다. */
  preview?: boolean;
}

/**
 * 상품 상세 모바일 하단 바의 버튼 줄 — 찜 · 장바구니(아이콘) · 문의하기 · 구매하기.
 * MobileBookingDrawer 와 플랫폼 문의 시트 안내 그림이 같은 모양을 쓰도록 분리했다.
 */
export default function ProductActionButtons({
  cartItemCount = 0,
  isChatLoading = false,
  onChatClick,
  isWishlisted = false,
  isWishlistLoading = false,
  onWishlistClick,
  wrapDrawerTrigger = (button) => button,
  preview = false,
}: ProductActionButtonsProps) {
  const t = useTranslations("product.booking");

  return (
    // 순서: 찜 · 장바구니(아이콘) · 문의하기 · 구매하기 — 주 행동은 엄지가 닿는 오른쪽 끝
    <div
      className="flex items-center gap-2"
      inert={preview || undefined}
      aria-hidden={preview || undefined}
    >
      {(onWishlistClick || preview) && (
        <button
          type="button"
          onClick={onWishlistClick}
          disabled={isWishlistLoading}
          aria-label={t("wishlist")}
          aria-pressed={isWishlisted}
          className={cn(
            "flex h-11 w-9 shrink-0 items-center justify-center rounded-xl transition-transform active:scale-95 active:bg-gray-100",
            isWishlisted ? "text-red-500" : "text-gray-600",
          )}
        >
          <HeartIcon
            weight={isWishlisted ? "fill" : "regular"}
            className="h-5 w-5"
          />
        </button>
      )}
      {wrapDrawerTrigger(
        <button
          type="button"
          aria-label={t("addToCart")}
          className="relative flex h-11 w-9 shrink-0 items-center justify-center rounded-xl text-gray-600 transition-transform active:scale-95 active:bg-gray-100"
        >
          <ToteIcon className="h-5 w-5" />
          {cartItemCount > 0 && (
            <span className="absolute top-1 -right-0.5 min-w-4 rounded-full bg-orange-500 px-1 text-[10px] leading-4 font-semibold text-white tabular-nums">
              {cartItemCount}
            </span>
          )}
        </button>,
      )}
      <button
        type="button"
        onClick={onChatClick}
        disabled={isChatLoading}
        className={cn(
          "flex h-11 flex-1 items-center justify-center rounded-xl bg-orange-50 text-base font-semibold text-orange-700 ring-1 ring-orange-200 ring-inset transition-transform active:scale-[0.97] active:bg-orange-100 disabled:opacity-70",
          preview && "ring-2 ring-orange-500",
        )}
      >
        {isChatLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("inquire")}
      </button>
      {wrapDrawerTrigger(
        <button
          type="button"
          className={cn(
            "flex h-11 flex-1 items-center justify-center rounded-xl bg-orange-500 text-base font-semibold text-white ring-1 ring-white/30 ring-inset transition-transform active:scale-[0.97] active:bg-orange-600",
            preview && "opacity-40",
          )}
        >
          {t("purchase")}
        </button>,
      )}
    </div>
  );
}
